<template>
    <div :class="'windows windows--' + state">
        <div class="windows__layer"></div>

        <!-- through the slot there may be added static window components, so they don't need to be loaded dynamically -->
        <slot />

        <!-- dynamically loaded/added windows -->
        <component v-for="window in windows" :is="window.component" :key="window.key" v-bind="window.props"></component>

        <div ref="loader" class="loader hidden">
            <spinner></spinner>
        </div>
    </div>
</template>

<script>
import Base from './Base';
import { getCsrfToken } from '../../helpers/forms';

export default {
    extends: Base,
    data() {
        return {
            state: 'closed',
            windows: [],
            openingArgs: null
        }
    },
    created() {
        this.$root.$on('window.opened', windowOpened => {
            this.getWindowsChildren().forEach(window => {
                if(window != windowOpened && window.state == 'visible') {
                    window.state = 'beneath';
                }
            });

            if(this.state == 'opening') {
                this.state = 'opened';
            }
        });

        this.$root.$on('window.closed', closedEvent => {
            let anyVisible = false;
            let windowBeneath = null;

            this.$children.forEach(child => {
                let window = this.getWindowFromChild(child);

                if(window.state == 'visible' || window.state == 'beneath') {
                    if(window.state == 'beneath') {
                        windowBeneath = window;
                    }
                    anyVisible = true;
                }
            });

            const closedWindow = closedEvent.window;
            if(closedWindow.args.closeResolve) {
                closedWindow.args.closeResolve(closedEvent.result);
            }

            if(!anyVisible) {
                this.state = 'closed';
            } else if(windowBeneath) {
                windowBeneath.state = 'visible';
            }
        });
    },
    updated() {
        if(this.state == 'opening' && this.openingArgs) {
            this.showWindow();
        }

        const html = document.getElementsByTagName('html')[0];

        if(this.state == 'opened') {
            html.classList.add('windows-opened');
        } else if(this.state == 'closed') {
            html.classList.remove('windows-opened');
        }
    },
    methods: {
        async showWindow(id = null, props = {}, data = {}, settings = {}) {
            let args = this.openingArgs;
            let ret = null;

            if(!args) {
                if(!id) {
                    throw new Error('No window id or component given');
                }

                let triggerElement = null;

                if(props instanceof HTMLElement) {
                    triggerElement = props;
                    props = {};
                } else if(data instanceof HTMLElement) {
                    triggerElement = data;
                    data = {};
                } else if (settings instanceof HTMLElement) {
                    triggerElement = settings;
                    settings = {};
                }

                settings = Object.assign({
                    reload: false,
                    resetOnClose: true,
                    onClose: null,
                    triggerElement
                }, settings);
                
                let closeResolve = null;
                ret = new Promise(resolve => {
                    closeResolve = resolve;
                });

                args = {
                    id,
                    settings,
                    props,
                    data,
                    component: null,
                    closeResolve
                };
            }

            if(!args.component && id && typeof id == 'object') {
                // allow passing plain vue component objects as a window to show
                // only works if its a single file component with a window component as root element in the template
                const windowComponent = id;

                if(windowComponent.props && windowComponent.props.id) {
                    args.id = windowComponent.props.id;
                } else if('provide' in windowComponent) {
                    const provides = typeof windowComponent.provide == 'function' ? windowComponent.provide() : windowComponent.provide;
                    
                    if(typeof provides == 'object' && provides.windowId) {
                        args.id = provides.windowId;
                    }
                } 
                
                if(args.id == windowComponent) {
                    throw new Error('Invalid window component given.');
                }

                args.component = windowComponent;
            }

            // get window component info
            let window = null;

            if('window' in args && args.window) {
                window = args.window;
                delete args.window;
            } else {
                window = this.findWindow(args.id);
            }

            // load it if not found or reload is set
            if(!window || args.settings.reload) {
                if(!args.component || args.settings.reload) {
                    // if the window to show does not exist in the windows list and we do not have a component for it, or the 'reload' setting is set to true, we'll try to (re)load it
                    this.$refs.loader.classList.remove('hidden');

                    if(window && window.child.state == 'visible') {
                        args.data.state = 'visible';
                        window.child.state = 'loading';
                    }

                    args.component = await this.loadWindow(args.id, args.data);
                    args.settings.reload = false; // do not reload it again

                    this.$refs.loader.classList.add('hidden');
                }

                if(args.component) {
                    this.addWindow(args, window);
                    window = null;
                } else {
                    throw new Error(`Could not find or load the window component for id "${args.id}"`)
                }
            } else if(!args.component && window && window.args) {
                args.component = window.args.component;
            }

            if(this.state != 'opening') {
                // store the needed args temporary and set state to 'opening' - this will trigger an update and call this method again through the updated hook (see above)
                // we need this mechanic for dynamically added windows - to make sure that the window is mounted before we search for its child and show it
                if(window && window.index != -1) {
                    // update args if window exists already and has been added dynamically (= window.index is not -1)
                    this.addWindow(args, window);
                    this.openingArgs = args;
                    this.openingArgs.window = window;
                    window = null;
                } else if(!window) {
                    this.openingArgs = args;
                }
                
                this.state = 'opening';
            } 
            
            if(window && this.state == 'opening') {
                // set the window's data and call the show method
                Object.assign(window.child, {
                    resetOnClose: args.settings.resetOnClose,
                    onClose: args.settings.onClose,
                });
                Object.assign(window.child.args, args);

                window.child.data = args.data;

                window.child.show(args.settings.triggerElement || null);

                this.openingArgs = null;
            }

            return ret;
        },

        addWindow(args, oldWindow = null) {
            if(oldWindow && oldWindow.index != -1) {
                this.windows.splice(oldWindow.index, 1, args);
            } else {
                this.windows.push(args);
            }
        },

        loadWindow(id, data) {
            return new Promise((resolve, reject) => {
                let key = id + ':';
                let postData = new FormData();

                for(let k in data) {
                    if(data[k] != undefined) {
                        postData.append(k, data[k]);

                        if(typeof data[k] == 'string' || typeof data[k] == 'number') {
                            key += k + '=' + data[k] + ',';
                        }
                    }
                }

                fetch('/windows/' + id, {
                    method: 'POST',
                    body: postData,
                    headers: {
                        'X-CSRF-TOKEN': getCsrfToken()
                    }
                }).then(resp => {
                    return resp.text()
                }).then(html => {
                    let compiled = Vue.compile(html);
                    compiled.key = key;
                    resolve(compiled);
                });
            });
        },

        findWindow(id) {
            let windowChild = null;
            let windowChildIndex = this.windows.findIndex(w => w.id == id);
            let windowArgs = windowChildIndex != -1 ? this.windows[windowChildIndex] : null;

            this.getWindowsChildren().every((window, index) => {
                if(window.id == id) {
                    windowChild = window;
                }

                return windowChild == null;
            });

            return windowChild ? {
                child: windowChild,
                index: windowChildIndex,
                args: windowArgs
            } : null;
        },

        getWindowsChildren() {
            return this.$children.filter(c => 
                (c.$options && c.$options._componentTag == 'window')
                || (c.$children && c.$children.length && c.$children[0].$options._componentTag == 'window')
            ).map(c => this.getWindowFromChild(c));
        },

        getWindowFromChild(child) {
            if(child.$children && child.$children.length && child.$children[0].$options._componentTag == 'window') {
                // dynamically loaded/added windows will always have a common component as parent, so lets get the actual window child..
               return child.$children[0];
            }

            return child;
        }
    }
}
</script>

<style lang="scss">
.windows {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 30;
   
    &__layer {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.7);
        z-index: 12;
    }

    > .loader {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translateX(-50%) translateY(-50%);
    }

    .window {
        position: absolute;
        z-index: 13;

        &--beneath {
            z-index: 11;
        }
    }

    &--closed {
        display: none;

        .window {
            display: none;
        }
    }

    &--opened {
        display: flex;
        justify-content: center;
        align-items: center;

        .window:not(.leaving):not(.window--beneath) {
            position: relative;
            left: auto !important;
            top: auto !important;
        }
    }

    &:not(#{&}--opened) {
        .window {
            overflow: hidden !important;
        }
    }
}
</style>